import { useState, useEffect, useCallback, useRef } from "react";
import {
  Upload, Trash2, Plus, RefreshCw, Save, Edit, Edit3, X, FileText,
  AlertCircle, CheckCircle2, Home, Image, Camera, ExternalLink,
  Clock, Download, Eye, Receipt, Wallet, Euro, Loader2, Lock,
  Building2, MapPin, Users, Phone, Mail, Calendar, Shield,
  User, History, ArrowLeft, ArrowRight, Wrench, MessageSquare,
  ChevronDown, ChevronUp, Ban, Send, Copy, Smartphone, Key, QrCode,
  ShieldCheck, ShieldAlert, ShieldX, Thermometer, Bot, Sparkles
} from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { BlockCard } from "../../../components/BlockCard";
import { Switch } from "../../../components/ui/switch";
import { Textarea } from "../../../components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription
} from "../../../components/ui/sheet";
import AgentPanel from "../../../components/agents/AgentPanel";
import { VerifyDevisDALButton } from "../../../components/dal/VerifyDevisDALButton";
import { API, getAuthHeaders, formatMontant } from "./helpers";
import { PreparerDepotPanel } from "./PreparerDepotPanel";
import { phaseUtile, demandeAnnulable, urlPhaseUtile } from "./phaseUtile";
import { toast } from "sonner";

export function ConformiteEditableTab({ dossier, tenantId, isTEF, onUpdate, planTrigger }) {
  const dossierId = dossier?.id;
  const dossierRef = dossier?.reference || dossier?.ref || "";
  const conformiteData = dossier?.workflow_data?.conformité || {};
  
  // State local pour édition
  const [localChecks, setLocalChecks] = useState({});
  // Source de chaque check : "agent_depot" | "manual" (undefined = non renseigné)
  const [checkSources, setCheckSources] = useState({});
  // Valeurs proposées par le dernier run agent (pour détecter "Modifié" si l'utilisateur change)
  const [agentProposed, setAgentProposed] = useState({});
  const [saving, setSaving] = useState(false);
  
  // ANAH dates state
  const [anahDates, setAnahDates] = useState(null);
  const [loadingAnah, setLoadingAnah] = useState(false);

  // CC-DEV1-15 — sections en <BlockCard> repliables. « Dates ANAH » ouverte par
  // défaut (info clé) ; Détail 24 pièces / Historique repliés par défaut.
  const [datesOpen, setDatesOpen] = useState(true);

  // Agent Dépôt — Phase 2 : Sheet + historique + auto-remplissage checkboxes
  const [agentSheetOpen, setAgentSheetOpen] = useState(false);
  const [agentHistory, setAgentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // AG-03 — Détail 24 pièces ANAH (lecture seule, alimenté par GET /checklist)
  const [pieceDetail, setPieceDetail] = useState(null); // { score_pct, required_total, required_present, present:[], missing_required:[], missing_optional:[], sources_used:[] }
  const [loadingPieceDetail, setLoadingPieceDetail] = useState(false);

  // useCallback AVANT useEffect (règle TDZ stricte)
  // AG-03 — Charge le détail 24 pièces sans run LLM (rapide, ~1-2s)
  const fetchPieceDetail = useCallback(async () => {
    if (!dossierRef) return;
    setLoadingPieceDetail(true);
    try {
      const headers = getAuthHeaders(tenantId);
      const res = await fetch(
        `${API}/api/metier/agents/depot/checklist?reference=${encodeURIComponent(dossierRef)}`,
        { headers, credentials: "include" }
      );
      if (res.ok) {
        const data = await res.json();
        if (data.found) {
          setPieceDetail({
            score_pct: data.score_pct,
            required_total: data.required_total,
            required_present: data.required_present,
            verdict: data.verdict,
            present: data.pieces_presentes || [],
            missing_required: data.pieces_manquantes_obligatoires || [],
            missing_optional: data.pieces_manquantes_optionnelles || [],
            sources_used: data.sources_used || [],
          });
        }
      }
    } catch (e) {
      console.error("[ConformiteEditableTab] piece detail fetch failed:", e);
    } finally {
      setLoadingPieceDetail(false);
    }
  }, [dossierRef, tenantId]);

  const fetchAgentHistory = useCallback(async () => {
    if (!dossierRef) return;
    setLoadingHistory(true);
    try {
      const headers = getAuthHeaders(tenantId);
      const res = await fetch(
        `${API}/api/metier/agents/depot/history?reference=${encodeURIComponent(dossierRef)}&limit=10`,
        { headers, credentials: "include" }
      );
      if (res.ok) {
        const data = await res.json();
        setAgentHistory(data.items || []);
      }
    } catch (e) {
      console.error("[ConformiteEditableTab] history fetch failed:", e);
    } finally {
      setLoadingHistory(false);
    }
  }, [dossierRef, tenantId]);

  // Mapping ANAH piece_id / eligibility_key → checkbox key du composant
  // (cf. AUDIT Phase 2 + backend/metier_hub/ai_agents/depot/tools/check_anah_checklist.py)
  const applyAgentChecks = useCallback((compliance) => {
    if (!compliance) return { filled: 0, proposed: {} };

    const mpa = compliance.mpa_checks || {};
    const elig = compliance.eligibility || {};

    // ANAH piece_id → tef_items_mpa.key
    // Si plusieurs pièces mappent vers la même clé, OR logique (true si l'une est présente)
    const ANAH_TO_KEY = {
      DAL: "dal_present",
      AI1: "avis_imposition",
      AI2: "avis_imposition",
      PI: "piece_identite",
      TP: "justif_propriete",
      MDA: "mandat_amo",
      DI: "devis_installateur",
    };

    // eligibility key → tef_items_elig.key
    const ELIG_TO_KEY = {
      elig_age: "age_eligible",
      elig_handicap: "handicap_eligible",
      elig_proprietaire: "proprietaire_occupant",
      elig_residence_principale: "residence_principale",
      elig_revenus: "revenus_modestes",
    };

    const proposed = {};

    // MPA — agréger par OR (true si au moins une pièce mappée à la clé est présente)
    Object.entries(mpa).forEach(([pid, info]) => {
      if (pid === "_meta") return;
      const key = ANAH_TO_KEY[pid];
      if (!key) return;
      const v = info?.value;
      if (v === true) proposed[key] = true;
      else if (v === false && proposed[key] !== true) proposed[key] = false;
    });

    // Eligibility
    Object.entries(elig).forEach(([ek, info]) => {
      if (ek === "_meta") return;
      const key = ELIG_TO_KEY[ek];
      if (!key) return;
      const v = info?.value;
      if (typeof v === "boolean") proposed[key] = v;
    });

    const filled = Object.keys(proposed).length;
    if (!filled) return { filled: 0, proposed: {} };

    // Merge dans localChecks + tagger source "agent_depot".
    // RÈGLE HUMAIN PRIORITAIRE : si une clé a déjà été taguée "manual",
    // ni la source ni la valeur ne sont écrasées. L'agent ne propose que.
    setLocalChecks(prev => {
      const next = { ...prev };
      Object.entries(proposed).forEach(([k, v]) => {
        if (checkSources[k] !== "manual") next[k] = v;
      });
      return next;
    });
    setCheckSources(prev => {
      const next = { ...prev };
      Object.keys(proposed).forEach(k => {
        if (next[k] !== "manual") next[k] = "agent_depot";
      });
      return next;
    });
    // agentProposed conserve toutes les propositions agent (même celles ignorées),
    // utile pour afficher le badge "Modifié" quand l'utilisateur va à l'encontre.
    setAgentProposed(prev => ({ ...prev, ...proposed }));
    return { filled, proposed };
  }, [checkSources]);

  const handleAgentComplete = useCallback((data) => {
    // Phase 2 : auto-remplir les checkboxes à partir de compliance_checks
    const { filled } = applyAgentChecks(data?.compliance_checks);
    if (filled > 0) {
      toast.success(`Agent Dépôt : ${filled} champ(s) auto-rempli(s)`, {
        description: "Pense à cliquer sur Sauvegarder pour persister.",
      });
    } else {
      toast.info("Agent Dépôt : aucun champ auto-rempli");
    }
    // AG-03 — Rafraîchir le détail 24 pièces (pour voir les nouvelles pièces classées)
    fetchPieceDetail();
    // Rafraîchir uniquement l'historique agent (pas le dossier — sinon l'init useEffect
    // écraserait localChecks/checkSources avec les valeurs serveur non sauvées).
    fetchAgentHistory();
  }, [applyAgentChecks, fetchAgentHistory, fetchPieceDetail]);
  
  // Initialiser depuis le dossier
  useEffect(() => {
    const mpa = conformiteData.mpa || {};
    const meta = conformiteData._meta || {};
    if (isTEF) {
      setLocalChecks({ ...mpa });
    } else {
      setLocalChecks({
        ...(conformiteData.cee || {}),
        ...(conformiteData.mpr || {}),
      });
    }
    // Charger les sources persistées (ex: champs déjà IA depuis une précédente sauvegarde)
    const initialSources = {};
    Object.keys(meta).forEach(k => {
      if (meta[k]?.source) initialSources[k] = meta[k].source;
    });
    setCheckSources(initialSources);
  }, [dossier]);

  // Charger les dates ANAH
  useEffect(() => {
    if (!dossierId || !isTEF) return;
    const fetchAnahDates = async () => {
      setLoadingAnah(true);
      try {
        const headers = getAuthHeaders(tenantId);
        const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/anah-status`, {
          headers, credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          setAnahDates(data);
        }
      } catch (e) {
        console.error("Erreur chargement dates ANAH:", e);
      } finally {
        setLoadingAnah(false);
      }
    };
    fetchAnahDates();
  }, [dossierId, tenantId, isTEF]);

  // Charger l'historique de l'Agent Dépôt pour ce dossier
  useEffect(() => {
    fetchAgentHistory();
  }, [fetchAgentHistory]);

  // AG-03 — Charger le détail 24 pièces au load (sans run LLM)
  useEffect(() => {
    fetchPieceDetail();
  }, [fetchPieceDetail]);

  // ─── Toggle "Dossier déposé" — section Dates ANAH ───
  // ON  → POST /marquer-depose : date_depot=today + statut="dossier_depose"
  // OFF → POST /annuler-depot  : date_depot=null + revert statut précédent
  const [togglingDepot, setTogglingDepot] = useState(false);
  const isDepose = !!(dossier?.date_depot || dossier?.date_depot_anah);
  // T-371d : la demande de paiement utile (avance ou solde) est décidée par le serveur, sur l'étape du dossier
  const [phaseDemande, setPhaseDemande] = useState(null);
  const [demandeAnnulableOk, setDemandeAnnulableOk] = useState(false);
  useEffect(() => {
    if (!isTEF || !dossierRef) { setPhaseDemande(null); setDemandeAnnulableOk(false); return undefined; }
    let annule = false;
    (async () => {
      try {
        const res = await fetch(urlPhaseUtile(API, dossierRef), { headers: getAuthHeaders(tenantId), credentials: "include" });
        const rep = res.ok ? await res.json() : null;
        if (!annule) { setPhaseDemande(phaseUtile(rep)); setDemandeAnnulableOk(demandeAnnulable(rep)); }
      } catch { if (!annule) { setPhaseDemande(null); setDemandeAnnulableOk(false); } }
    })();
    return () => { annule = true; };
  }, [isTEF, dossierRef, tenantId, dossier?.statut_anah, dossier?.statut]);

  const handleDepotToggle = async (checked) => {
    if (togglingDepot) return;
    setTogglingDepot(true);
    try {
      const headers = getAuthHeaders(tenantId, "application/json");
      const path = checked ? "marquer-depose" : "annuler-depot";
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/${path}`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({}),
      });
      if (res.ok) {
        toast.success(checked ? "Dossier marqué comme déposé" : "Dépôt annulé");
        onUpdate?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur lors du toggle");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setTogglingDepot(false);
    }
  };

  const toggleCheck = (key) => {
    setLocalChecks(prev => {
      const newVal = !prev[key];
      // Si l'utilisateur modifie une valeur proposée par l'agent → tagger "manual"
      setCheckSources(prevSrc => ({ ...prevSrc, [key]: "manual" }));
      return { ...prev, [key]: newVal };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = getAuthHeaders(tenantId, "application/json");
      // Reconstruire _meta avec timestamp pour chaque champ tracé
      const nowIso = new Date().toISOString();
      const metaPatch = {};
      Object.entries(checkSources).forEach(([k, source]) => {
        metaPatch[k] = {
          source,
          updated_at: nowIso,
        };
      });
      const payload = isTEF
        ? {
            workflow_data: {
              conformité: {
                ...conformiteData,
                mpa: localChecks,
                _meta: { ...(conformiteData._meta || {}), ...metaPatch },
              }
            }
          }
        : { workflow_data: { conformité: { ...conformiteData, cee: localChecks, mpr: localChecks } } };

      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}`, {
        method: "PATCH",
        headers,
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        toast.success("Conformité mise à jour");
        onUpdate?.();
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setSaving(false);
    }
  };

  const tef_items_mpa = [
    { label: "DAL (Diagnostic Adaptation Logement)", key: "dal_present" },  // G-CONF2 : harmonisé avec la config (Adaptation, pas Autonomie)
    { label: "Avis d'imposition", key: "avis_imposition" },
    { label: "Pièce d'identité", key: "piece_identite" },
    { label: "Justificatif propriété", key: "justif_propriete" },
    { label: "Mandat AMO signé", key: "mandat_amo" },
    {
      label: "Devis installateur",
      key: "devis_installateur",
      // CC-17 fix bug AG-02 : bouton « Comparer DAL ↔ Devis » accessible
      // depuis Conformité dès qu'un devis est chargé. Trigger MANUEL
      // uniquement — AG-02 ne génère pas ces alertes en auto.
      action: (
        <VerifyDevisDALButton
          dossierId={dossierId}
          tenantId={tenantId}
          hasDevis={
            !!dossier?.montant_devis ||
            (dossier?.devis_ids && dossier.devis_ids.length > 0)
          }
          className="ml-2"
        />
      ),
    },
  ];
  // CC-DEV1-12 — l'AFFICHAGE « Éligibilité du dossier » (5 critères) est retiré de
  // Conformité : Conformité ne valide QUE les pièces. L'éligibilité reste dans
  // Infos → MaPrimeAdapt'. La DONNÉE n'est PAS touchée : les 5 clés
  // (age_eligible, handicap_eligible, proprietaire_occupant, residence_principale,
  // revenus_modestes — cf. ELIG_TO_KEY ci-dessus) continuent d'être écrites par
  // l'Agent Dépôt dans workflow_data.conformité.mpa et persistées par handleSave.
  const climat_items_cee = [
    { label: "Attestation sur l'honneur", key: "attestation_honneur" },
    { label: "Devis conforme BAR-TH", key: "devis_conforme" },
    { label: "Facture détaillée", key: "facture_detaillee" },
    { label: "Certificat RGE entreprise", key: "cert_rge" }
  ];
  const climat_items_mpr = [
    { label: "Avis d'imposition", key: "avis_imposition" },
    { label: "Justificatif de propriété", key: "justif_propriete" },
    { label: "Devis validé ANAH", key: "devis_anah" },
    { label: "Attestation de fin de travaux", key: "attestation_fin" }
  ];

  // Calcul du statut global — Conformité = validation des PIÈCES uniquement.
  // L'éligibilité bénéficiaire (âge/handicap/occupation/résidence/revenus) se
  // valide dans l'onglet Infos → MaPrimeAdapt' (mockup-conformite-v2) : retirée
  // du décompte ici (mais l'Agent Dépôt continue de l'analyser pour Infos).
  const allItems = isTEF ? [...tef_items_mpa] : [...climat_items_cee, ...climat_items_mpr];
  const checkedCount = allItems.filter(i => localChecks[i.key]).length;
  const totalCount = allItems.length;
  const globalPct = totalCount > 0 ? Math.round((checkedCount / totalCount) * 100) : 0;

  // Décompte des cases manuelles ci-dessous. NE SERT PLUS DE VERDICT (P0-E) :
  // ces 6 cases ne couvrent que 7 des 13 pièces ANAH (DAL/AI1/AI2/PI/TP/MDA/DI).
  // CV (convention AMO), PF (plan financement), RIB/RIB_MAND et PROC n'y figurent
  // pas — un 6/6 laissait donc 3 pièces BLOQUANTES jamais vérifiées et affichait
  // « Tous les éléments sont conformes » à tort.
  const piecesChecked = tef_items_mpa.filter(i => localChecks[i.key]).length;
  const piecesTotal = tef_items_mpa.length;

  // CC-DEV1-19 — décompte RÉEL de la checklist ANAH (détail AG-03), au lieu du
  // « 24 » figé/périmé (CC-AUDIT-21). Dynamique = total des pièces applicables
  // renvoyées par GET /checklist (présentes + manquantes obl. + opt.). Fallback
  // 13 = taille checklist TEF (migration 026) tant que le détail n'est pas chargé.
  const anahPiecesCount = pieceDetail
    ? (pieceDetail.present.length + pieceDetail.missing_required.length + pieceDetail.missing_optional.length)
    : 13;

  // ── P0-E — SOURCE UNIQUE DU VERDICT DE CONFORMITÉ (TEF) ────────────────
  // Le verdict vient du moteur ANAH `check_anah_checklist` (config
  // `metier_config_listes` type=anah_pieces_required, migration 026), exposé
  // par GET /api/metier/agents/depot/checklist et déjà chargé dans pieceDetail.
  // Le moteur évalue les conditions par dossier (owner_only, mandataire_only…),
  // donc le total est DYNAMIQUE (~10 pièces bloquantes) et non « 6 » ni « 13 ».
  // Tant que le moteur n'a pas répondu, on n'affirme JAMAIS la conformité.
  const anahReady = !!pieceDetail;
  const anahPresent = pieceDetail?.required_present ?? 0;
  const anahTotal = pieceDetail?.required_total ?? 0;
  const anahVerdict = pieceDetail?.verdict ?? null;

  // Pourcentage affiché : moteur ANAH pour TEF, décompte local pour Climat
  // (régime CEE/MPR — pas de checklist ANAH applicable).
  const displayPct = isTEF ? (anahReady ? (pieceDetail.score_pct ?? 0) : null) : globalPct;
  // Conforme = verdict moteur « PRÊT » (aucune pièce bloquante manquante).
  const isConforme = isTEF ? anahVerdict === "PRÊT" : globalPct === 100;

  const renderCheckItem = (item) => {
    const isChecked = !!localChecks[item.key];
    const source = checkSources[item.key]; // "agent_depot" | "manual" | undefined
    // Badge "Modifié" si l'agent avait proposé une valeur et l'utilisateur a changé manuellement
    const wasAgentProposed = item.key in agentProposed;
    const isModifiedFromAgent =
      source === "manual" && wasAgentProposed && agentProposed[item.key] !== isChecked;
    const isAgentFilled = source === "agent_depot";
    return (
      <div key={item.key} className="space-y-2">
        <button
          onClick={() => toggleCheck(item.key)}
          className="flex items-center gap-3 w-full p-2 rounded-lg text-left transition-colors"
          style={{ background: isChecked ? "rgba(16,185,129,0.06)" : "transparent" }}
          data-testid={`conformité-check-${item.key}`}
        >
          <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
            isChecked ? "bg-emerald-500 text-[var(--oh-accent-contrast)]" : ""
          }`} style={!isChecked ? { border: "2px solid var(--oh-border)", background: "transparent" } : {}}>
            {isChecked && <CheckCircle2 className="w-3.5 h-3.5" />}
          </div>
          <span className="text-sm flex-1" style={{ color: isChecked ? "var(--oh-text)" : "var(--oh-text-muted)" }}>
            {item.label}
          </span>
          {isAgentFilled && (
            <Badge
              className="border-0 text-[10px] inline-flex items-center gap-0.5"
              style={{ background: "var(--oh-stat-blue-bg)", color: "var(--oh-stat-blue-text)" }}
              data-testid={`badge-ia-${item.key}`}
            >
              <Sparkles className="w-2.5 h-2.5" /> IA
            </Badge>
          )}
          {isModifiedFromAgent && (
            <Badge
              className="border-0 text-[10px]"
              style={{ background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)" }}
              data-testid={`badge-modifie-${item.key}`}
            >
              Modifié
            </Badge>
          )}
          {isChecked && (
            <Badge className="border-0 text-[10px]" style={{ background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)" }} data-testid={`badge-ok-${item.key}`}>OK</Badge>
          )}
        </button>
        {/* Action contextuelle (ex: bouton « Comparer DAL ↔ Devis » sur l'item devis_installateur) */}
        {item.action && (
          <div className="pl-8" data-testid={`conformité-action-${item.key}`}>
            {item.action}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="rounded-xl p-6" style={{ background: "var(--oh-bg-elevated)", border: "1px solid rgba(59,130,246,0.2)" }} data-testid="conformité-editable">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-medium flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
          <Shield className="w-5 h-5" style={{ color: "var(--oh-stat-blue-text)" }} />
          Vérification Conformité
        </h3>
        <div className="flex items-center gap-3 flex-wrap">
          {isTEF ? (
            // P0-E — compteur = pièces ANAH bloquantes réellement exigées pour CE
            // dossier (moteur AG-03), plus le « X/6 » des cases manuelles.
            <Badge className="border-0" style={{ background: "var(--oh-stat-blue-bg)", color: "var(--oh-stat-blue-text)" }} data-testid="conformite-pieces-count">
              {anahReady
                ? `Pièces ANAH obligatoires : ${anahPresent}/${anahTotal}`
                : "Pièces ANAH : analyse en cours…"}
            </Badge>
          ) : (
            <Badge className="border-0" style={{ background: "var(--oh-stat-blue-bg)", color: "var(--oh-stat-blue-text)" }}>
              {checkedCount}/{totalCount}
            </Badge>
          )}
          {isTEF && dossierRef && (
            <Button
              size="sm"
              onClick={() => setAgentSheetOpen(true)}
              data-testid="open-agent-depot-btn"
            >
              <Sparkles className="w-3 h-3 mr-1" />
              Analyser avec Agent Dépôt
            </Button>
          )}
          {/* CC-DEV1-17 #3 — boutons primary alignés sur la charte (variant default =
              fond primary + texte blanc forcé, composant partagé CC-DEV1-15). Fin des
              bg-blue/bg-emerald hardcodés peu lisibles. */}
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-conformité-btn"
          >
            {saving ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
            Sauvegarder
          </Button>
        </div>
      </div>

      {/* Barre de progression */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Progression conformité</span>
          <span className="text-xs font-medium" style={{ color: displayPct === null ? 'var(--oh-text-muted)' : displayPct >= 80 ? 'var(--oh-stat-green-text)' : displayPct >= 50 ? 'var(--oh-stat-amber-text)' : 'var(--oh-stat-red-text)' }}>
            {displayPct === null ? "—" : `${displayPct}%`}
          </span>
        </div>
        <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--oh-bg-surface)" }}>
          <div
            className={`h-full rounded-full transition-all ${(displayPct ?? 0) >= 80 ? 'bg-emerald-500' : (displayPct ?? 0) >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
            style={{ width: `${displayPct ?? 0}%` }}
          />
        </div>
      </div>

      {/* V1-DEP (T-242b) — Préparer le dépôt : paquet + fiche, puis « Déposé » */}
      {isTEF && dossierRef && (
        <PreparerDepotPanel
          dossierId={dossierId}
          dossierRef={dossierRef}
          tenantId={tenantId}
          isDepose={isDepose}
          onUpdate={onUpdate}
        />
      )}

      {/* T-371 (S-2) — après le dépôt : préparer la demande d'avance, puis la demande de solde (même moteur, listes de pièces de la phase) */}
      {/* T-371c : UNE seule demande à la fois, celle qui est utile — le solde une fois l'avance versée ou les travaux terminés, sinon l'avance une fois l'octroi
          daté. Ni les deux, ni avant le dépôt : un seul bouton principal en plus de celui du dépôt, et une seule vérification de pièces à l'ouverture. */}
      {isTEF && dossierRef && [phaseDemande].filter(Boolean).map((phase) => (
        <PreparerDepotPanel
          key={phase}
          phase={phase}
          dateDemande={phase === "avance" ? dossier?.date_demande_avance : dossier?.date_demande_solde}
          dossierId={dossierId}
          dossierRef={dossierRef}
          tenantId={tenantId}
          isDepose={isDepose}
          annulable={demandeAnnulableOk}
          onUpdate={onUpdate}
        />
      ))}

      {/* Dates ANAH — section clé conformité */}
      {isTEF && (
        <BlockCard
          collapsible
          open={datesOpen}
          onToggle={(e) => setDatesOpen(e.currentTarget.open)}
          icon={Building2}
          iconColor="var(--oh-stat-amber-text)"
          title="Dates ANAH"
          testId="conformité-anah-dates"
          badge={
            anahDates?.anah_linked ? (
              <Badge className="border-0 text-[10px]" style={{ background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)" }}>Lié</Badge>
            ) : anahDates?.anah_reference ? (
              <Badge className="border-0 text-[10px]" style={{ background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)" }}>Ref: {anahDates.anah_reference}</Badge>
            ) : null
          }
        >
          {loadingAnah ? (
            <div className="flex items-center gap-2 text-sm" style={{ color: "var(--oh-text-muted)" }}>
              <RefreshCw className="w-3 h-3 animate-spin" /> Chargement...
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {/* Date dépôt ANAH — avec toggle pour marquer/annuler le dépôt */}
              <div className="p-2.5 rounded-md" style={{ background: "var(--oh-bg-elevated)" }} data-testid="conformite-date-depot-toggle">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px]" style={{ color: "var(--oh-text-muted)" }}>Date dépôt ANAH</p>
                  <Switch
                    checked={isDepose}
                    onCheckedChange={handleDepotToggle}
                    disabled={togglingDepot}
                    aria-label={isDepose ? "Annuler le dépôt" : "Marquer le dossier comme déposé"}
                    data-testid="depot-toggle"
                  />
                </div>
                {(() => {
                  // Priorité : date côté dossier (source de vérité après toggle) → fallback ANAH/manual
                  const date = dossier?.date_depot
                    || dossier?.date_depot_anah
                    || anahDates?.anah_data?.date_depot
                    || anahDates?.manual_data?.date_depot;
                  return (
                    <p className={`text-sm font-medium ${date ? '' : 'italic'}`}
                       style={{ color: date ? "var(--oh-text)" : "var(--oh-text-muted)" }}>
                      {date || "—"}
                    </p>
                  );
                })()}
              </div>
              {/* Autres dates (read-only) */}
              {[
                { label: "Date instruction", value: anahDates?.manual_data?.date_instruction || anahDates?.anah_data?.date_mise_a_jour, key: "date_instruction" },
                { label: "Date acceptation", value: anahDates?.manual_data?.date_acceptation, key: "date_acceptation" },
                { label: "Date fin travaux", value: anahDates?.manual_data?.date_fin_travaux, key: "date_fin_travaux" },
                { label: "Date paiement", value: anahDates?.manual_data?.date_paiement, key: "date_paiement" },
                { label: "Statut ANAH", value: anahDates?.anah_data?.statut_anah_label, key: "statut_anah" },
              ].map(item => (
                <div key={item.key} className="p-2.5 rounded-md" style={{ background: "var(--oh-bg-elevated)" }}>
                  <p className="text-[10px] mb-1" style={{ color: "var(--oh-text-muted)" }}>{item.label}</p>
                  <p className={`text-sm font-medium ${item.value ? '' : 'italic'}`} style={{ color: item.value ? "var(--oh-text)" : "var(--oh-text-muted)" }}>
                    {item.value || "—"}
                  </p>
                </div>
              ))}
            </div>
          )}
          {!anahDates?.anah_reference && !loadingAnah && (
            <p className="text-xs mt-2 italic" style={{ color: "var(--oh-text-muted)" }}>
              Aucune référence ANAH liée. Allez dans l'onglet "Aides MPA" pour lier un dossier ANAH.
            </p>
          )}
        </BlockCard>
      )}

      {isTEF ? (
        // CC-DEV1-12 — Conformité = validation des PIÈCES uniquement. Le bloc
        // « Éligibilité du dossier » (5 critères) est retiré : l'éligibilité
        // s'affiche dans Infos → MaPrimeAdapt'. Carte pièces en pleine largeur.
        // CC-DEV1-15 — non repliable : checklist principale toujours visible.
        <BlockCard icon={ShieldCheck} iconColor="var(--oh-stat-cyan-text)" title="MaPrimeAdapt' (ANAH)" testId="conformite-pieces-mpa">
          <div className="space-y-1">{tef_items_mpa.map(renderCheckItem)}</div>
        </BlockCard>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          <BlockCard icon={ShieldCheck} iconColor="var(--oh-stat-green-text)" title="CEE" testId="conformite-pieces-cee" marginBottom={0}>
            <div className="space-y-1">{climat_items_cee.map(renderCheckItem)}</div>
          </BlockCard>
          <BlockCard icon={ShieldCheck} iconColor="var(--oh-stat-purple-text)" title="MaPrimeRénov'" testId="conformite-pieces-mpr" marginBottom={0}>
            <div className="space-y-1">{climat_items_mpr.map(renderCheckItem)}</div>
          </BlockCard>
        </div>
      )}

      {/* Statut global — P0-E : pour TEF le verdict vient du moteur ANAH
          (pieceDetail), jamais du décompte des 6 cases manuelles. */}
      <div
        className="mt-6 p-4 rounded-lg"
        data-testid="conformite-statut-global"
        style={{
          background: isConforme ? "var(--oh-stat-green-bg)" : "var(--oh-stat-blue-bg)",
          border: `1px solid ${isConforme ? "var(--oh-stat-green-border)" : "var(--oh-stat-blue-border)"}`,
        }}
      >
        <div className="flex items-center gap-3">
          {isConforme ? (
            <CheckCircle2 className="w-6 h-6" style={{ color: "var(--oh-stat-green-text)" }} />
          ) : (
            <Shield className="w-6 h-6" style={{ color: "var(--oh-stat-blue-text)" }} />
          )}
          <div>
            <p className="font-medium" style={{ color: "var(--oh-text)" }}>
              {isConforme ? "Conformité validée" : "Statut de conformité"}
            </p>
            <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>
              {isTEF ? (
                !anahReady
                  ? "Analyse des pièces ANAH en cours — conformité non déterminée."
                  : isConforme
                    ? `Les ${anahTotal} pièces ANAH obligatoires sont présentes.`
                    : `${anahPresent} sur ${anahTotal} pièces ANAH obligatoires — ${pieceDetail.missing_required.length} manquante(s) bloquante(s)${
                        pieceDetail.missing_required.length
                          ? " : " + pieceDetail.missing_required.map(p => p.nom).join(", ")
                          : ""
                      }.`
              ) : (
                isConforme
                  ? "Tous les éléments sont conformes"
                  : `${checkedCount} sur ${totalCount} éléments validés — ${totalCount - checkedCount} restant(s)`
              )}
            </p>
            {isTEF && anahReady && (
              <p className="text-xs mt-1" style={{ color: "var(--oh-text-muted)" }}>
                Verdict Agent Dépôt : {anahVerdict} · cases manuelles ci-dessous : {piecesChecked}/{piecesTotal}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Slot Plan de financement (mockup-conformite : Statut → Plan → 24 pièces
          → Historique). Rendu ICI pour respecter l'ordre, fourni par DossierDetail. */}
      {planTrigger}

      {/* AG-03 — Détail 24 pièces ANAH (lecture seule) — BlockCard repliable */}
      {isTEF && dossierRef && (
        <BlockCard
          collapsible
          icon={FileText}
          iconColor="var(--oh-stat-blue-text)"
          title={`Détail ${anahPiecesCount} pièces ANAH`}
          testId="agent-depot-piece-detail"
          badge={pieceDetail && (
            <Badge
              className="border-0 text-[10px]"
              style={{ background: "var(--oh-stat-blue-bg)", color: "var(--oh-stat-blue-text)" }}
              data-testid="piece-detail-score"
            >
              {pieceDetail.required_present}/{pieceDetail.required_total} obligatoires
            </Badge>
          )}
          action={
            <span className="flex items-center gap-2">
              {pieceDetail?.verdict && (
                <Badge
                  className="text-[10px] border"
                  style={
                    pieceDetail.verdict === "PRÊT"
                      ? { background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)", borderColor: "var(--oh-stat-green-border)" }
                      : pieceDetail.verdict === "BLOQUÉ"
                      ? { background: "var(--oh-stat-red-bg)", color: "var(--oh-stat-red-text)", borderColor: "var(--oh-stat-red-border)" }
                      : { background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)", borderColor: "var(--oh-stat-amber-border)" }
                  }
                  data-testid="piece-detail-verdict"
                >
                  {pieceDetail.verdict}
                </Badge>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={fetchPieceDetail}
                disabled={loadingPieceDetail}
                className="text-xs"
                aria-label={`Rafraîchir le détail des ${anahPiecesCount} pièces ANAH`}
                data-testid="refresh-piece-detail-btn"
              >
                <RefreshCw className={`w-3 h-3 mr-1 ${loadingPieceDetail ? "animate-spin" : ""}`} />
                Rafraîchir
              </Button>
            </span>
          }
        >

          {loadingPieceDetail && !pieceDetail ? (
            <p className="text-xs italic" style={{ color: "var(--oh-text-muted)" }}>Chargement…</p>
          ) : !pieceDetail ? (
            <p className="text-xs italic" style={{ color: "var(--oh-text-muted)" }} data-testid="piece-detail-empty">
              Aucune donnée. Clique sur Rafraîchir pour analyser le dossier.
            </p>
          ) : (
            (() => {
              const all = [
                ...pieceDetail.present.map(p => ({ ...p, status: "present" })),
                ...pieceDetail.missing_required.map(p => ({ ...p, status: "missing_required" })),
                ...pieceDetail.missing_optional.map(p => ({ ...p, status: "missing_optional" })),
              ];
              const CAT_LABELS = {
                identite_propriete: "Identité & Propriété",
                ressources: "Ressources",
                logement: "Logement",
                amo_convention: "Convention AMO",
                travaux_devis: "Travaux & Entreprise",
                financement: "Financement",
                dossier_anah: "Dossier ANAH",
                photos: "Photos",
                cloture: "Clôture",
              };
              const CAT_ORDER = [
                "identite_propriete", "ressources", "logement", "amo_convention",
                "travaux_devis", "financement", "dossier_anah", "photos", "cloture",
              ];
              const grouped = {};
              all.forEach(p => {
                const cat = p.category || "autre";
                if (!grouped[cat]) grouped[cat] = [];
                grouped[cat].push(p);
              });

              return (
                <div className="space-y-3" data-testid="piece-detail-grid">
                  {CAT_ORDER.filter(cat => grouped[cat]).map(cat => (
                    <div key={cat}>
                      <p className="text-[11px] uppercase tracking-wide mb-1.5" style={{ color: "var(--oh-text-muted)" }}>
                        {CAT_LABELS[cat] || cat}
                      </p>
                      <ul className="space-y-1">
                        {grouped[cat].map(piece => {
                          const isPresent = piece.status === "present";
                          const isMissingReq = piece.status === "missing_required";
                          return (
                            <li
                              key={piece.id}
                              className="flex items-center gap-2 px-2 py-1.5 rounded text-xs"
                              style={{ background: "var(--oh-bg-elevated)" }}
                              data-testid={`piece-${piece.id}`}
                            >
                              {isPresent && (
                                <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--oh-stat-green-text)" }} />
                              )}
                              {isMissingReq && (
                                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--oh-stat-red-text)" }} />
                              )}
                              {!isPresent && !isMissingReq && (
                                <Ban className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--oh-text-muted)" }} />
                              )}
                              <span className="font-mono text-[10px]" style={{ color: "var(--oh-text-muted)" }}>
                                {piece.id}
                              </span>
                              <span className="flex-1" style={{ color: "var(--oh-text)" }}>
                                {piece.nom}
                              </span>
                              {piece.required ? (
                                <Badge
                                  className="border-0 text-[9px]"
                                  style={{ background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)" }}
                                >
                                  Obligatoire
                                </Badge>
                              ) : (
                                <Badge className="border-0 text-[9px]" style={{ background: "var(--oh-bg-surface)", color: "var(--oh-text-muted)" }}>Optionnel</Badge>
                              )}
                              {isPresent && piece.source_document && (
                                <span
                                  className="truncate max-w-[180px] text-[10px]"
                                  style={{ color: "var(--oh-text-muted)" }}
                                  title={`${piece.source_document} (${piece.source})`}
                                >
                                  {piece.source_document}
                                </span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                  {pieceDetail.sources_used && pieceDetail.sources_used.length > 0 && (
                    <p className="text-[10px] italic mt-2" style={{ color: "var(--oh-text-muted)" }}>
                      Sources analysées : {pieceDetail.sources_used.join(", ")}
                    </p>
                  )}
                </div>
              );
            })()
          )}
        </BlockCard>
      )}

      {/* Historique Agent Dépôt — Phase 1 — BlockCard repliable */}
      {isTEF && dossierRef && (
        <BlockCard
          collapsible
          icon={History}
          iconColor="var(--oh-stat-blue-text)"
          title="Historique Agent Dépôt"
          testId="agent-depot-history-block"
          badge={agentHistory.length > 0 && (
            <Badge className="border-0 text-[10px]" style={{ background: "var(--oh-stat-blue-bg)", color: "var(--oh-stat-blue-text)" }}>
              {agentHistory.length}
            </Badge>
          )}
          action={
            <Button
              size="sm"
              variant="ghost"
              onClick={fetchAgentHistory}
              disabled={loadingHistory}
              className="text-xs"
              data-testid="refresh-agent-history-btn"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${loadingHistory ? "animate-spin" : ""}`} />
              Rafraîchir
            </Button>
          }
        >

          {loadingHistory && agentHistory.length === 0 ? (
            <p className="text-xs italic" style={{ color: "var(--oh-text-muted)" }}>
              Chargement…
            </p>
          ) : agentHistory.length === 0 ? (
            <p className="text-xs italic" style={{ color: "var(--oh-text-muted)" }} data-testid="agent-history-empty">
              Aucune analyse effectuée pour ce dossier.
            </p>
          ) : (
            <ul className="space-y-2" data-testid="agent-history-list">
              {agentHistory.map((run) => {
                const verdictStyle =
                  run.verdict === "PRÊT" ? { background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)", borderColor: "var(--oh-stat-green-border)" }
                    : run.verdict === "BLOQUÉ" ? { background: "var(--oh-stat-red-bg)", color: "var(--oh-stat-red-text)", borderColor: "var(--oh-stat-red-border)" }
                    : run.verdict === "ATTENTION" ? { background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)", borderColor: "var(--oh-stat-amber-border)" }
                    : { background: "var(--oh-bg-surface)", color: "var(--oh-text-secondary)", borderColor: "var(--oh-border)" };
                let dateLabel = run.timestamp;
                try {
                  dateLabel = new Date(run.timestamp).toLocaleString("fr-FR", {
                    day: "2-digit", month: "2-digit", year: "numeric",
                    hour: "2-digit", minute: "2-digit"
                  });
                } catch { /* keep raw */ }
                return (
                  <li
                    key={run.session_id}
                    className="rounded-md p-2.5 text-xs"
                    style={{ background: "var(--oh-bg-elevated)" }}
                    data-testid={`agent-history-item-${run.session_id}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="border text-[10px] font-medium" style={verdictStyle}>
                        {run.verdict}
                      </Badge>
                      <span style={{ color: "var(--oh-text-muted)" }}>{dateLabel}</span>
                      <span className="inline-flex items-center gap-1" style={{ color: "var(--oh-text-muted)" }}>
                        <Clock className="w-3 h-3" />
                        {(run.duration_ms / 1000).toFixed(1)}s
                      </span>
                      <span style={{ color: "var(--oh-text-muted)" }}>
                        {run.iterations} iter
                      </span>
                    </div>
                    {run.analysis_preview && (
                      <p
                        className="mt-1.5 leading-relaxed whitespace-pre-wrap"
                        style={{ color: "var(--oh-text)" }}
                      >
                        {run.analysis_preview}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </BlockCard>
      )}

      {/* Sheet — Agent Dépôt */}
      {isTEF && (
        <Sheet open={agentSheetOpen} onOpenChange={setAgentSheetOpen}>
          <SheetContent
            side="right"
            className="sm:max-w-xl w-full bg-[var(--oh-bg)] border-l border-[var(--oh-border)] overflow-hidden flex flex-col"
            data-testid="agent-depot-sheet"
          >
            <SheetHeader>
              <SheetTitle className="text-[var(--oh-text)] flex items-center gap-2">
                <Bot className="w-5 h-5 text-[var(--oh-accent-text)]" />
                Agent Dépôt — Conformité ANAH
              </SheetTitle>
              <SheetDescription className="text-[var(--oh-text-secondary)]">
                Analyse automatisée du dossier <span className="font-mono">{dossierRef}</span> pour le dépôt MaPrimeAdapt'.
              </SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-hidden mt-4">
              <AgentPanel
                agent="depot"
                context={{ reference: dossierRef }}
                onComplete={handleAgentComplete}
                mode="embedded"
                tenantId={tenantId}
              />
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
